// Shared with the Make.com "samples-approval" scenario — Make computes
// sha256(payload + KEY) there and this recomputes the same thing. Not a
// real secret (it ships in this public asset file) — it exists only so
// the approved-SKU list in the URL isn't a plain, obviously-editable
// query param. See integrations/make-com/samples-approval/README.md for
// the exact token format.
const CLAIM_SAMPLES_KEY = '72e406d46eec4aa615cc3d0ff9d377724b0740fbc8a92d10';

// Always added to every claim-samples order alongside the customer's picks
// (NF-REF-FLY-V1, NF-B2B-INS-V1) — visible as $0 line items at checkout for now.
// TODO(open loop): move these to a post-checkout Order Edit API call so they
// don't appear as removable cart line items. See OPEN-LOOPS.md.
const ALWAYS_INCLUDED_VARIANT_IDS = ['48761751765236', '48761750618356'];

// Backend for links that carry a claim token (&c=). Validates the token,
// refuses a link whose samples were already ordered, and creates the
// Shopify draft order checkout. Served by the portal (newflora-llc/nf-app
// lib/samples-claim.ts, builds/intake.md), a port of the retired n8n workflow nf-samples-claim
// (newflora-llc/ops archive/n8n/), so the page does not depend on n8n.
const CLAIM_SAMPLES_API = 'https://portal.newflora.com/api/intake/samples-claim';

async function callClaimApi(payload) {
  const response = await fetch(CLAIM_SAMPLES_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return response.json();
}

async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function verifyAndParseToken() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('k');
  if (!token) return null;

  const dotIndex = token.lastIndexOf('.');
  if (dotIndex === -1) return null;

  const payloadB64 = token.slice(0, dotIndex);
  const signature = token.slice(dotIndex + 1);
  const expectedSignature = (await sha256Hex(payloadB64 + CLAIM_SAMPLES_KEY)).slice(0, 16);
  if (signature !== expectedSignature) return null;

  let decoded;
  try {
    decoded = atob(payloadB64);
  } catch (e) {
    return null;
  }

  const parts = decoded.split('|');
  if (parts.length !== 3) return null;

  const [email, discount, skusJoined] = parts;
  const skus = skusJoined
    .split(',')
    .map((code) => code.trim().toLowerCase())
    .filter(Boolean);

  if (!email || !discount || !skus.length) return null;

  const claimToken = (params.get('c') || '').trim().toLowerCase();

  return { email, discount, skus, claimToken };
}

class ClaimSamplesForm extends HTMLElement {
  async connectedCallback() {
    const approval = await verifyAndParseToken();
    if (!approval) {
      window.location.replace('/');
      return;
    }
    this.approval = approval;

    // Links minted since the draft-order checkout carry a claim token. Ask the
    // backend first: unknown token -> homepage, already ordered -> message.
    // If the check itself can't be reached, show the picker anyway; the
    // checkout call re-validates the token.
    if (approval.claimToken) {
      try {
        const result = await callClaimApi({ token: approval.claimToken, action: 'status' });
        if (result.status === 'claimed') {
          this.showClaimed();
          return;
        }
        if (result.status === 'invalid') {
          window.location.replace('/');
          return;
        }
      } catch (e) {
        // fall through to the picker
      }
    }

    this.cards = Array.from(this.querySelectorAll('.claim-samples__card'));
    this.groups = Array.from(this.querySelectorAll('.claim-samples__group'));
    this.submitButton = this.querySelector('.claim-samples__submit');
    this.countLabel = this.querySelector('.claim-samples__count');

    if (!this.applyApprovedSkus()) {
      window.location.replace('/');
      return;
    }

    this.cards.forEach((card) => {
      const input = card.querySelector('.claim-samples__input');

      if (input.type === 'radio') {
        // Native radios can only be turned on by a click, never off. Track
        // whether this one was already checked right before the click so we
        // can force it back off when the user clicks an already-selected
        // option, letting a "choose one" group be fully deselected too.
        card.addEventListener('pointerdown', () => {
          input.dataset.wasChecked = input.checked ? 'true' : '';
        });
        input.addEventListener('click', () => {
          if (input.dataset.wasChecked === 'true') {
            input.checked = false;
            card
              .closest('.claim-samples__group')
              .querySelectorAll('.claim-samples__card')
              .forEach((otherCard) => otherCard.classList.remove('is-selected'));
            this.updateSubmitState();
          }
        });
      }

      input.addEventListener('change', () => {
        if (input.type === 'radio') {
          card
            .closest('.claim-samples__group')
            .querySelectorAll('.claim-samples__card')
            .forEach((otherCard) => otherCard.classList.toggle('is-selected', otherCard === card));
        } else {
          card.classList.toggle('is-selected', input.checked);
        }
        this.updateSubmitState();
      });
    });

    this.errorLabel = this.querySelector('.claim-samples__error');
    this.submitButton.addEventListener('click', () => this.goToCheckout());
    this.updateSubmitState();
    this.hidden = false;
  }

  applyApprovedSkus() {
    let anyVisible = false;
    this.cards.forEach((card) => {
      const code = (card.dataset.code || '').toLowerCase();
      const isApproved = this.approval.skus.includes(code);
      card.hidden = !isApproved;
      if (isApproved) anyVisible = true;
    });

    this.groups.forEach((group) => {
      const visibleCount = group.querySelectorAll('.claim-samples__card:not([hidden])').length;
      group.hidden = visibleCount === 0;
    });

    return anyVisible;
  }

  updateSubmitState() {
    const selectedCount = this.cards.filter(
      (card) => !card.hidden && card.querySelector('.claim-samples__input').checked
    ).length;
    this.submitButton.disabled = selectedCount === 0;
    if (this.countLabel) {
      this.countLabel.textContent =
        selectedCount === 0
          ? 'No samples selected yet'
          : `${selectedCount} sample${selectedCount === 1 ? '' : 's'} selected`;
    }
  }

  showClaimed() {
    const page = this.closest('.claim-samples__page');
    const heading = page.querySelector('.claim-samples__heading');
    if (heading) heading.hidden = true;
    const subheading = page.querySelector('.claim-samples__subheading');
    if (subheading) subheading.hidden = true;
    const panel = page.querySelector('.claim-samples__claimed');
    if (panel) panel.hidden = false;
    this.hidden = true;
  }

  showError(message) {
    if (!this.errorLabel) return;
    this.errorLabel.textContent = message;
    this.errorLabel.hidden = false;
  }

  async goToDraftOrderCheckout() {
    const selectedSkus = this.cards
      .filter((card) => !card.hidden && card.querySelector('.claim-samples__input').checked)
      .map((card) => (card.dataset.code || '').toUpperCase())
      .filter(Boolean);

    if (!selectedSkus.length) return;

    const buttonLabel = this.submitButton.textContent;
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Preparing your checkout…';
    if (this.errorLabel) this.errorLabel.hidden = true;

    try {
      const result = await callClaimApi({
        token: this.approval.claimToken,
        action: 'create',
        skus: selectedSkus,
      });

      if (result.status === 'created' && result.url) {
        window.location.href = result.url;
        return;
      }
      if (result.status === 'claimed') {
        this.showClaimed();
        return;
      }
      if (result.status === 'invalid') {
        window.location.replace('/');
        return;
      }
      this.showError(
        result.status === 'invalid_selection'
          ? 'That combination of samples isn\'t available. Please adjust your selection and try again.'
          : 'Something went wrong preparing your checkout. Please try again, or reply to your approval email and we\'ll help.'
      );
    } catch (e) {
      this.showError(
        'Something went wrong preparing your checkout. Please try again, or reply to your approval email and we\'ll help.'
      );
    }

    this.submitButton.textContent = buttonLabel;
    this.updateSubmitState();
  }

  goToCheckout() {
    if (this.approval.claimToken) {
      this.goToDraftOrderCheckout();
      return;
    }

    const selectedVariantIds = this.cards
      .filter((card) => !card.hidden && card.querySelector('.claim-samples__input').checked)
      .map((card) => card.dataset.variantId)
      .filter(Boolean);

    if (!selectedVariantIds.length) return;

    this.submitButton.disabled = true;

    // Shopify's cart reverses line-item order when multiple items are added
    // in one call (confirmed via direct /cart/add.js testing) — so the
    // extras go first in this list to land last in checkout's summary.
    const cartPath = [...ALWAYS_INCLUDED_VARIANT_IDS, ...selectedVariantIds]
      .map((id) => `${id}:1`)
      .join(',');
    let checkoutUrl = `${window.location.origin}/cart/${cartPath}`;

    const checkoutParams = new URLSearchParams();
    checkoutParams.set('discount', this.approval.discount);
    checkoutParams.set('checkout[email]', this.approval.email);

    checkoutUrl += `?${checkoutParams.toString()}`;
    window.location.href = checkoutUrl;
  }
}

customElements.define('claim-samples-form', ClaimSamplesForm);
